import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { MessageSquarePlus, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface CreateChatDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateChat: (title: string) => void;
  caseId: string;
}

const CreateChatDialog: React.FC<CreateChatDialogProps> = ({
  open,
  onClose,
  onCreateChat,
  caseId
}) => {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (error) setError('');
  };

  const handleCreate = () => {
    if (!title.trim()) {
      setError(t('errors.validation.conversationTitleRequired'));
      return;
    }
    onCreateChat(title);
    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setError('');
    onClose();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleClose();
    }
  };

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[425px]"
        dir={getLanguageDirection(i18n.language)}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gold/10">
              <MessageSquarePlus className="w-5 h-5 text-gold" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              {t('common.dialogs.createChat.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {t('common.dialogs.createChat.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="chat-title">{t('common.dialogs.createChat.chatTitleLabel')}</Label>
            <Input
              id="chat-title"
              ref={inputRef}
              value={title}
              onChange={handleTitleChange}
              placeholder={t('dialogs.createChat.placeholder')}
            />
            {error && (
              <div className="flex items-center gap-1.5 text-sm text-destructive">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
          >
            {t('common.dialogs.createChat.cancel')}
          </Button>
          <div className="w-1"></div>
          <Button onClick={handleCreate}>
            {t('common.dialogs.createChat.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateChatDialog;
